import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-4">分类不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://caoliuhome.com/' },
          { '@type': 'ListItem', position: 2, name: category.name },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${category.name} - 草榴回家的路`,
        description: category.description,
        url: `https://caoliuhome.com/category/${slug}`,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs" />
                <span className="text-foreground-900">{category.name}</span>
              </nav>
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className={`${category.icon} text-xl text-primary-600`} />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
                    {category.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                          {article.categoryName}
                        </span>
                        <span className="text-xs text-foreground-400">{article.date}</span>
                      </div>
                      <h3 className="text-base font-heading text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-foreground-400">
                        <i className="ri-time-line" />
                        <span>{article.readTime} 分钟阅读</span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-foreground-500">暂无相关文章</p>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}